import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuthentication } from '@/context/AuthenticationProvider';
import { useConfig } from '@/context/ConfigProvider';

const Login = () => {
  const { signIn, completeNewPassword } = useAuthentication();
  const config = useConfig();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  /**
   * Set when Cognito has accepted the temporary password and is waiting for a permanent one. An
   * invited user always lands here on first sign-in, because `adminCreateUser` issues a temporary
   * password — so this is the normal path for everyone except the seeded dev admin.
   */
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const mismatch =
    confirmPassword.length > 0 && newPassword !== confirmPassword;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      if (needsNewPassword) {
        if (newPassword !== confirmPassword) {
          setError('The two passwords do not match.');
          return;
        }
        await completeNewPassword(newPassword);
        return;
      }

      const step = await signIn(email.trim(), password);
      if (step === 'newPassword') {
        setNeedsNewPassword(true);
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Sign-in failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        // Restated here rather than inherited: the login screen renders outside the app shell.
        backgroundImage: (theme) =>
          `radial-gradient(circle at 30% 20%, ${theme.palette.primary.dark}33, transparent 60%)`,
      }}
    >
      <Card sx={{ width: 400, maxWidth: '90vw' }} elevation={8}>
        <CardContent>
          <Stack component="form" gap={2.5} onSubmit={(e) => void submit(e)}>
            <Stack direction="row" gap={1.5} alignItems="center">
              <img src="/favicon.svg" alt="" width="40" height="40" />
              <Stack>
                <Typography variant="h6">VMS</Typography>
                <Typography variant="caption" color="text.secondary">
                  Video Metadata Store · {config.env}
                </Typography>
              </Stack>
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}

            {needsNewPassword ? (
              <>
                <Alert severity="info">
                  Your invitation used a temporary password. Choose a permanent
                  one to finish signing in.
                </Alert>
                <TextField
                  label="New password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  required
                  fullWidth
                  helperText="At least 8 characters, with upper and lower case, a number and a symbol."
                />
                <TextField
                  label="Confirm new password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  fullWidth
                  error={mismatch}
                  helperText={mismatch ? 'Does not match.' : ' '}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={
                    busy || !newPassword || !confirmPassword || mismatch
                  }
                >
                  {busy ? 'Setting password…' : 'Set password and sign in'}
                </Button>
              </>
            ) : (
              <>
                <TextField
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  autoFocus
                  required
                  fullWidth
                />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  fullWidth
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={busy || !email || !password}
                >
                  {busy ? 'Signing in…' : 'Sign in'}
                </Button>
              </>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
